import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
} from "ag-grid-enterprise";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";
import {
  LinkCellRenderer,
  ManualIconCellRenderer,
} from "../../../../controls/ag-grid/components/ButtonCellRenderer";
import { CheckboxRenderer } from "../../../../controls/ag-grid/components/CheckboxRenderer";
import { ProcessesButtons } from "../../../../controls/buttons/ProcessesButtons";
import type { ProcessByShift } from "../models/ProcessByShift";

interface TableHookProps {
  linkData: (data: ProcessByShift) => void;
  unlinkData: (data: ProcessByShift) => void;
  toggleStatusData: (data: ProcessByShift) => void;
  setShowCommentModal: (
    show: boolean,
    data: ProcessByShift,
    position?: { top: number; left: number },
  ) => void;
  showContextMenu: (
    e: React.MouseEvent,
    data: ProcessByShift,
    configId: string,
  ) => void;
}

export const useTableData = ({
  linkData,
  unlinkData,
  toggleStatusData,
  setShowCommentModal,
  showContextMenu,
}: TableHookProps) => {
  const columnDefs: ColDef[] | ColGroupDef = [
    {
      field: "HeatName",
      headerName: "Heat",
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/meltshop-heats/${params.data.HeatShiftId}?heatName=${params.data.HeatName}`,
        });
      },
      flex: 1,
      minWidth: 60,
    },
    {
      field: "GradeName",
      headerName: "Grade",
      flex: 1,
      minWidth: 90,
    },
    {
      field: "LadleName",
      headerName: "Ladle",
      flex: 1,
      minWidth: 60,
    },
    {
      field: "Crew",
      headerName: "Crew",
      flex: 1,
      minWidth: 40,
    },
    {
      headerName: "Times",
      children: [
        {
          field: "Start",
          headerName: "Arrive",
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.Start, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "LadleOpen",
          headerName: "Open",
          cellRenderer: (params: ICellRendererParams) => {
            return LinkCellRenderer({
              ...params,
              link: `/caster-data-entry/${params.data.ProcessId}`,
            });
          },
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.LadleOpen, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "LadleClose",
          headerName: "Close",
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.LadleClose, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "End",
          headerName: "End",
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.End, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "Duration",
          headerName: "Dur",
          valueGetter: (params) => params.data?.Duration?.toFixed(1) || "",
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      field: "MoldSize",
      headerName: "Size",
      flex: 1,
      minWidth: 35,
    },
    {
      field: "CastTons",
      headerName: "Tons",
      type: ["numericColumn", "numericWidth"],
      valueGetter: (params) => params.data?.CastTons || "0",
      flex: 1,
      minWidth: 65,
    },
    {
      headerName: "Temperatures",
      children: [
        {
          field: "LastLMFTemperature",
          headerName: "LMF",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) =>
            params.data?.LastLMFTemperature?.toFixed(0) || "",
          flex: 1,
          minWidth: 55,
        },
        {
          field: "FirstTemperature",
          headerName: "First",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) =>
            params.data?.FirstTemperature?.toFixed(0) || "",
          flex: 1,
          minWidth: 55,
        },
        {
          field: "LastTemperature",
          headerName: "Last",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) =>
            params.data?.LastTemperature?.toFixed(0) || "",
          flex: 1,
          minWidth: 55,
        },
        {
          field: "LastSuperHeat",
          headerName: "Last SH",
          headerTooltip: "Last Super Heat",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) => params.data?.LastSuperHeat?.toFixed(0) || "",
          flex: 1,
          minWidth: 55,
        },
      ],
    } as ColGroupDef,
    {
      field: "Length",
      headerName: "Length",
      flex: 1,
      minWidth: 70, //
    },
    {
      headerName: "Tundish",
      children: [
        {
          field: "TundishName",
          headerName: "Nr.",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "HeatsOnTundish",
          headerName: "#",
          headerTooltip: "Heats On Tundish",
          flex: 1,
          minWidth: 35,
        },
      ],
    },
    {
      headerName: "Strand 1",
      children: [
        {
          field: "MoldS1",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "SpeedS1",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "TotalCutsS1",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 35,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 2",
      children: [
        {
          field: "MoldS2",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "SpeedS2",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "TotalCutsS2",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 35,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 3",
      children: [
        {
          field: "MoldS3",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "SpeedS3",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "TotalCutsS3",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 35,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 4",
      children: [
        {
          field: "MoldS4",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "SpeedS4",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "TotalCutsS4",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 35,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 5",
      children: [
        {
          field: "MoldS5",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "SpeedS5",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 35,
        },
        {
          field: "TotalCutsS5",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 35,
        },
      ],
    } as ColGroupDef,
    {
      field: "IsManualOpen",
      headerName: "Manual Open",
      flex: 1,
      minWidth: 80,
      cellRenderer: CheckboxRenderer,
      type: ["centerAligned"],
    },
    {
      field: "IsLadleShrouded",
      headerName: "Ladle Shrouded",
      flex: 1,
      minWidth: 80,
      cellRenderer: CheckboxRenderer,
      type: ["centerAligned"],
    },
    {
      field: "Comments",
      headerName: "Notes",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        return CommentsColumn({
          data: params?.data,
          setShowOpenModal: setShowCommentModal,
        });
      },
      minWidth: 40,
    },
    {
      field: "IsManual",
      headerName: "",
      type: ["nonSorteable", "nonFilterable", "centerAligned"],
      cellRenderer: ManualIconCellRenderer,
      cellClass: "ag-button-cell",
      flex: 1,
      minWidth: 40,
    },
    {
      cellRenderer: ProcessesButtons, // link unlink commented in ProcessesButtons.tsx
      cellRendererParams: (params: ICellRendererParams) => ({
        params: params,
        linkData: linkData,
        unlinkData: unlinkData,
        toggleStatusData: toggleStatusData,
        showContextMenu: (event: React.MouseEvent) =>
          showContextMenu(event, params.data, "casterProcessesContext"),
      }),
      wrapText: false,
      wrapHeaderText: false,
      headerName: "",
      sortable: false,
      flex: 1,
      maxWidth: 150,
      minWidth: 150,
      filter: false,
    },
  ];

  return { columnDefs };
};
